import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../api";
import { getAuthItem, getAuthJson } from "../../../../utils/authStorage";

const AuthContext = createContext(null);

function paceUserFallback() {
  const stored =
    getAuthJson("user", null) ||
    getAuthJson("currentUser", null) ||
    {};
  const rawRole = String(
    stored.role || stored.userRole || getAuthItem("userType") || ""
  ).toLowerCase();
  const companyId =
    stored.companyId ||
    stored.company_id ||
    getAuthItem("companyId") ||
    getAuthJson("companyData", null)?.companyId;

  return {
    ...stored,
    id: stored.id || stored.userId || getAuthItem("userId"),
    role: rawRole === "admin" || rawRole === "super_admin" ? "admin" : "recruiter",
    name:
      stored.name ||
      stored.fullName ||
      [stored.firstName, stored.lastName].filter(Boolean).join(" ") ||
      "PaceHRM User",
    companyId,
  };
}

function normalizePortalUser(user) {
  if (!user) return null;
  const rawRole = String(user.role || "").toLowerCase();
  return {
    ...user,
    role:
      rawRole === "admin" || rawRole === "super_admin" ? "admin" : "recruiter",
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/auth/me");
      const portalUser = response.data?.user || response.data;
      const normalizedUser = normalizePortalUser(portalUser) || paceUserFallback();
      setUser(normalizedUser);
      return normalizedUser;
    } catch (error) {
      const fallback = paceUserFallback();
      setUser(fallback);
      return fallback;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  return (
    <AuthContext.Provider
      value={{ user, loading, logout: () => {}, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within HiringPortalProvider");
  }
  return context;
}
